import React, { useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AppsIcon from '@mui/icons-material/Apps';
import {
  BoxProps,
  Icon,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SwipeableDrawer,
  SwipeableDrawerProps,
} from '@mui/material';

import { INavigationRoutes, userNavigationRoutes } from '../../../navigation';
import { useAppSelector } from '../../../hooks';
import { getAuthState } from '../../../store/AuthSlice';
import { getAccessLimitState } from '../../../store/AccessLimitSlice';
import { checkUserPermission, SafeAreaGaps } from '../../../helper';
import NavBarContents from '../../../pages/UserPages/UserDashboard/components/NavBarContents';

// STYLES
const drawerStyles: SwipeableDrawerProps['sx'] = {
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    width: '90vw',
    maxWidth: '600px',
  },
};

const containerStyles: BoxProps['style'] = {
  paddingTop: `calc(${SafeAreaGaps.top} + 50px)`,
  paddingBottom: `calc(${SafeAreaGaps.bottom} + 15px)`,
  paddingLeft: `calc(${SafeAreaGaps.top} + 15px)`,
  paddingRight: `calc(${SafeAreaGaps.right} + 15px)`,
  height: '100%',
};

const SubTopicDrawerButton: React.FC<IconButtonProps> = (props) => {
  // HOOKS
  const navigate = useNavigate();
  const location = useLocation();

  // STORE
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);

  // PERMISSIONS
  const adminStudentPermission = checkUserPermission(authState.user, {
    adminStudent: ['view'],
  });

  const quizHistoryPermission = checkUserPermission(authState.user, {
    quizHistory: ['view'],
  });

  // LOCAL STATES
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // BOOLEANS
  const open = Boolean(anchorEl);

  // METHODS
  const toggleDrawer = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filterOutUnAuthorizedRoutes = (
    userNavigationRoute: INavigationRoutes
  ) => {
    if (userNavigationRoute.key === 'students' && !adminStudentPermission) {
      return false;
    }

    if (userNavigationRoute.key === 'quiz-history' && !quizHistoryPermission) {
      return false;
    }

    if (
      userNavigationRoute.key === 'subscriptions' &&
      !accessLimitState.lpAccess
    ) {
      return false;
    }

    return true;
  };

  const handleNavigationChange = (userNavigationRoute: INavigationRoutes) => {
    setTimeout(() => {
      navigate(userNavigationRoute.pathname);
    }, 250);

    handleClose();
  };

  return (
    <div>
      <IconButton {...props} onClick={toggleDrawer}>
        <MenuIcon />
      </IconButton>
      <SwipeableDrawer
        open={isDrawerOpen}
        sx={drawerStyles}
        onOpen={toggleDrawer}
        onClose={toggleDrawer}
      >
        <div style={containerStyles}>
          <div>
            <Stack direction="row" alignItems="center" sx={{ pb: '15px' }}>
              <IconButton
                id="mobile-user-nav-bar-menu-button"
                aria-controls={open ? 'mobile-user-nav-bar-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                size="large"
                edge="start"
                color="primary"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={handleClick}
              >
                <AppsIcon />
              </IconButton>
              <a href={`${process.env.PUBLIC_URL}/`}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/tp-logo-text-green.png`}
                  alt="tp-logo"
                  style={{ width: '120px' }}
                />
              </a>
            </Stack>
          </div>
          <div>
            <NavBarContents />
          </div>
        </div>
      </SwipeableDrawer>

      {/* MENU */}
      <Menu
        id="mobileuser-nav-bar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { borderRadius: '12px', minWidth: '300px' } }}
        MenuListProps={{
          'aria-labelledby': 'mobile-user-nav-bar-menu-button',
          style: { padding: '15px' },
        }}
      >
        {userNavigationRoutes
          .filter(filterOutUnAuthorizedRoutes)
          .map((userNavigationRoute, index) => {
            let isActive = false;

            if (
              userNavigationRoute.matches &&
              userNavigationRoute.matches.length > 0
            ) {
              userNavigationRoute.matches.forEach((match) => {
                if (matchPath(match, location.pathname)) {
                  isActive = true;
                }
              });
            }

            return (
              <MenuItem
                key={index}
                selected={isActive}
                onClick={() => handleNavigationChange(userNavigationRoute)}
                style={{ padding: '15px', borderRadius: '7px' }}
              >
                <ListItemIcon>
                  <Icon
                    color={isActive ? 'primary' : 'inherit'}
                    component={userNavigationRoute.icon}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={userNavigationRoute.name}
                  primaryTypographyProps={{
                    color: isActive ? 'primary' : 'secondary',
                  }}
                />
              </MenuItem>
            );
          })}
      </Menu>
    </div>
  );
};

export default SubTopicDrawerButton;
